/**
 * usePrintLabels Hook
 *
 * Handles print window creation, thermal printer CSS, and error recovery.
 * Uses window.open() + document.write() pattern from Figma app.
 *
 * BROWSER COMPATIBILITY:
 * - Chrome/Edge: Full @page support, best for thermal printers
 * - Firefox: Limited @page size support, may require manual paper selection
 * - Safari: Very limited @page support, PDF preview recommended
 *
 * For best results with thermal printers (Zebra, etc.), use Chrome.
 */

import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LABEL_DIMENSIONS } from '../utils/label-constants';
import {
  PrintErrorCode,
  PrintError,
  noLabelsError,
} from '../utils/print-errors';
import { printHtmlViaIframe } from '@/lib/print-utils';
import type { CanvasSize } from '@/features/production-settings/components/label-layout/label-layout-types';

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

interface UsePrintLabelsOptions {
  /** Called when print operation starts */
  onPrintStart?: () => void;
  /** Called when print operation completes successfully */
  onPrintComplete?: () => void;
  /** Called when print operation fails */
  onPrintError?: (error: PrintError) => void;
  /** Set to false to suppress the success toast (e.g. for picklist printing) */
  showSuccessToast?: boolean;
}

interface UsePrintLabelsReturn {
  /** Ref to attach to hidden print container */
  printRef: React.RefObject<HTMLDivElement>;
  /** Trigger print operation. Pass canvasSize (mm) to set @page size dynamically. */
  printLabels: (customStyles?: string, canvasSize?: CanvasSize, documentTitle?: string) => void;
  /** Whether print is in progress */
  isPrinting: boolean;
}

/**
 * Hook for printing labels via browser print dialog.
 *
 * @example
 * ```tsx
 * const { printRef, printLabels, isPrinting } = usePrintLabels({
 *   onPrintComplete: () => logPrintEvent(batchId),
 * });
 *
 * <div ref={printRef} style={{ display: 'none' }}>
 *   {garments.map(g => <GarmentLabel key={g.id} garment={g} />)}
 * </div>
 * <Button onClick={() => printLabels()} disabled={isPrinting}>Print</Button>
 * ```
 */
export function usePrintLabels(
  options: UsePrintLabelsOptions = {}
): UsePrintLabelsReturn {
  const { t } = useTranslation('production');
  const printRef = useRef<HTMLDivElement | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  // Store options in ref to avoid dependency issues
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const printLabels = useCallback((customStyles: string = '', canvasSize?: CanvasSize, documentTitle?: string) => {
    const { onPrintStart, onPrintComplete, onPrintError } = optionsRef.current;

    // Validate ref exists
    if (!printRef.current) {
      const error = noLabelsError();
      toast.error(t('print.noLabels'), {
        description: t('print.noLabelsDesc'),
      });
      onPrintError?.(error);
      return;
    }

    // Validate labels exist in container (not just any content)
    const labelCount = printRef.current.querySelectorAll('.label').length;
    if (labelCount === 0) {
      const error = noLabelsError();
      toast.error(t('print.noLabels'), {
        description: t('print.noValidLabels'),
      });
      onPrintError?.(error);
      return;
    }

    setIsPrinting(true);
    onPrintStart?.();

    try {
      // Compute @page size from canvas dimensions (mm -> inches) or fall back to label constants
      let pageWidth: string;
      let pageHeight: string;
      let orientation: string;
      if (canvasSize) {
        const wIn = (canvasSize.width / 25.4).toFixed(2);
        const hIn = (canvasSize.height / 25.4).toFixed(2);
        pageWidth = `${wIn}in`;
        pageHeight = `${hIn}in`;
        // Square labels (width === height) default to landscape -- safe for all printers
        orientation = canvasSize.height > canvasSize.width ? 'portrait' : 'landscape';
      } else {
        pageWidth = LABEL_DIMENSIONS.WIDTH;
        pageHeight = LABEL_DIMENSIONS.HEIGHT;
        orientation = '';
      }
      const pageOrientation = orientation ? ` ${orientation}` : '';

      // Build print HTML document
      // CRITICAL: @page CSS for thermal printer sizing
      // Browser support notes in header comment
      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta name="color-scheme" content="light only">
            <title>${escapeHtml(documentTitle || 'Garment Labels')}</title>
            <style>
              /* Reset */
              * { box-sizing: border-box; margin: 0; padding: 0; }
              body { font-family: "PingFang SC", "Microsoft YaHei", "Noto Sans SC", Arial, "Helvetica Neue", sans-serif; color: #000; background: #fff; }

              /*
               * THERMAL PRINTER PAGE SIZE
               *
               * Size is derived from the label template canvas dimensions.
               * Falls back to 4x3" when no canvas size is provided.
               *
               * Browser Support:
               * - Chrome 131+: Full support
               * - Edge: Full support
               * - Firefox: Limited - may require manual paper size in print dialog
               * - Safari 15.5+: Full support
               *
               * If labels print at wrong size, check:
               * 1. Printer driver has correct label size configured
               * 2. Print dialog shows correct paper size
               * 3. Scale is set to 100% (not "Fit to page")
               */
              @page {
                size: ${pageWidth} ${pageHeight}${pageOrientation};
                margin: 0;
              }

              /* Print-specific styles */
              @media print {
                .label {
                  page-break-inside: avoid;
                  /* Remove screen border for cleaner thermal print */
                  border: none !important;
                }
                .label + .label {
                  page-break-before: always;
                }
              }

              /* Custom styles passed by caller (e.g., picklist styles) */
              ${customStyles}
            </style>
          </head>
          <body>
            ${printRef.current.innerHTML}
          </body>
        </html>
      `;

      printHtmlViaIframe(printHTML, () => {
        // Called by afterprint event — fires when user prints OR cancels the dialog
        setIsPrinting(false);
        onPrintComplete?.();
      });

      // Show success toast with label size info (unless suppressed)
      if (optionsRef.current.showSuccessToast !== false) {
        const sizeInfo = canvasSize ? canvasSize.name : 'Standard (4x3")';
        toast.success(t('print.labelsSentToPrinter', { count: labelCount }), {
          description: t('print.labelsSentDesc', { size: sizeInfo }),
        });
      }
    } catch (err) {
      const error = new PrintError(
        PrintErrorCode.UNKNOWN,
        err instanceof Error ? err.message : 'Unknown print error'
      );
      toast.error(t('print.printFailed'), {
        description: t('print.printFailedDesc'),
      });
      onPrintError?.(error);
      setIsPrinting(false);
    }
  }, []);

  return { printRef, printLabels, isPrinting };
}

export default usePrintLabels;
